'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname, useRouter } from '../i18n/navigation';
import { routing } from '../i18n/routing';
import { FLAG_COMPONENTS } from './FlagIcons';

// Bandeira representa pais, nao idioma — por isso o codigo do idioma aparece
// junto e o nome completo vai no aria-label, senao "ES" fica ambiguo entre
// Espanha/America Latina e "EN" entre Reino Unido/EUA.
const LOCALES: Record<string, { code: string; name: string }> = {
  pt: { code: 'PT', name: 'Português' },
  en: { code: 'EN', name: 'English' },
  es: { code: 'ES', name: 'Español' },
};

export default function LanguageSwitcher() {
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const current = LOCALES[locale] ?? LOCALES[routing.defaultLocale];
  const CurrentFlag = FLAG_COMPONENTS[locale] ?? FLAG_COMPONENTS[routing.defaultLocale];

  function switchTo(target: string) {
    setOpen(false);
    // usePathname do next-intl devolve o caminho sem prefixo, entao a mesma
    // pagina e preservada na troca de idioma.
    router.replace(pathname, { locale: target });
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={t('nav.idiomaAria')}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1.5 p-2 hover:bg-gray-200 rounded transition-colors duration-150"
      >
        <CurrentFlag />
        <span style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.03em' }}>
          {current.code}
        </span>
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          aria-hidden="true"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t('nav.idiomaAria')}
          className="absolute right-0 mt-1 py-1 bg-white shadow-lg z-50"
          style={{ border: '1px solid #ddd', borderRadius: '4px', minWidth: '150px' }}
        >
          {routing.locales.map((loc) => {
            const info = LOCALES[loc];
            const Flag = FLAG_COMPONENTS[loc];
            const active = loc === locale;
            return (
              <li key={loc} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => switchTo(loc)}
                  lang={loc}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 transition-colors duration-150"
                  style={{ fontWeight: active ? 600 : 400 }}
                >
                  <Flag />
                  <span>{info.name}</span>
                  {active && (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      aria-hidden="true"
                      style={{ marginLeft: 'auto', color: '#1a9fff' }}
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
